/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect, useMemo } from 'react';
import { AuditStatus, ComplianceChecklist, ChecklistItem } from '../../types';
import { AUDIT_REQUIREMENTS } from '../../constants/orgManagement';
import { orgManagementService } from '../../services/orgManagementService';
import SOC2DashboardEnhanced from './SOC2DashboardEnhanced';
import SOC2ScenarioPlayer from './SOC2ScenarioPlayer';
import { EnterpriseDashboardShell } from './kit';

interface AuditDashboardEnhancedProps {
  onClose: () => void;
  budget: number;
}

interface ComplianceMetrics {
  overallScore: number;
  readinessLevel: 'needs_improvement' | 'developing' | 'adequate' | 'effective';
  trendDirection: 'improving' | 'stable' | 'declining';
  criticalGaps: number;
  remediationBacklog: number;
}

interface AuditTimeline {
  pastEvents: AuditEvent[];
  currentEvents: AuditEvent[];
  upcomingEvents: AuditEvent[];
  milestones: Milestone[];
  deadlines: Deadline[];
}

interface AuditEvent {
  id: string;
  type: 'audit' | 'checklist' | 'assessment' | 'remediation' | 'training';
  title: string;
  status: 'completed' | 'in_progress' | 'scheduled' | 'overdue';
  date: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee?: string;
  progress?: number;
  score?: number;
  findings?: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface Milestone {
  id: string;
  title: string;
  date: number;
  status: 'completed' | 'upcoming' | 'overdue';
  impact: 'low' | 'medium' | 'high';
  description: string;
}

interface Deadline {
  id: string;
  title: string;
  date: number;
  type: 'regulatory' | 'contractual' | 'internal';
  severity: 'low' | 'medium' | 'high' | 'critical';
  daysRemaining: number;
}

interface Finding {
  id: string;
  auditId: string;
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  recommendation: string;
  status: 'open' | 'in_progress' | 'remediated' | 'accepted_risk';
  assignee?: string;
  dueDate: number;
  created: number;
  riskImpact: number;
}

interface ComplianceReport {
  id: string;
  type: 'SOC2' | 'PCI' | 'Regulatory' | 'Internal' | 'Custom';
  title: string;
  generated: number;
  status: 'draft' | 'review' | 'final' | 'submitted';
  score: number;
  findings: Finding[];
  recommendations: string[];
  executiveSummary: string;
}

const AuditDashboardEnhanced: React.FC<AuditDashboardEnhancedProps> = ({ onClose, budget }) => {
  const [audits, setAudits] = useState<AuditStatus[]>([]);
  const [checklists, setChecklists] = useState<ComplianceChecklist[]>([]);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'audits' | 'compliance' | 'timeline' | 'reports' | 'soc2'>('dashboard');
  const [showSOC2Dashboard, setShowSOC2Dashboard] = useState(false);
  const [showSOC2Scenario, setShowSOC2Scenario] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<AuditStatus | null>(null);
  const [selectedChecklist, setSelectedChecklist] = useState<ComplianceChecklist | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list' | 'gantt'>('calendar');
  const [dateRange, setDateRange] = useState<'30d' | '90d' | '6m' | '1y'>('90d');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setAudits(orgManagementService.getAudits());
    setChecklists(orgManagementService.getChecklists());
  };

  // Advanced Compliance Metrics Calculation
  const complianceMetrics: ComplianceMetrics = useMemo(() => {
    const completedAudits = audits.filter(a => a.status === 'Completed');
    const avgScore = completedAudits.length > 0
      ? completedAudits.reduce((sum, a) => sum + a.score, 0) / completedAudits.length
      : 0;

    const totalFindings = audits.reduce((sum, a) =>
      sum + a.findings.critical + a.findings.high + a.findings.medium, 0
    );

    const checklistProgress = checklists.length > 0
      ? checklists.reduce((sum, c) => sum + c.progress, 0) / checklists.length
      : 0;

    const overallScore = Math.round((avgScore * 0.7 + checklistProgress * 0.3));

    const readinessLevel = overallScore >= 90 ? 'effective' :
                          overallScore >= 75 ? 'adequate' :
                          overallScore >= 60 ? 'developing' : 'needs_improvement';

    return {
      overallScore,
      readinessLevel,
      trendDirection: overallScore > 80 ? 'improving' : overallScore > 60 ? 'stable' : 'declining',
      criticalGaps: totalFindings,
      remediationBacklog: audits.filter(a => a.remediationRequired).length
    };
  }, [audits, checklists]);

  // Advanced Timeline Management
  const auditTimeline: AuditTimeline = useMemo(() => {
    const now = Date.now();
    const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
    const ninetyDaysFromNow = now + (90 * 24 * 60 * 60 * 1000);

    // Generate audit events
    const events: AuditEvent[] = [
      ...audits.map(audit => ({
        id: audit.id,
        type: 'audit' as const,
        title: `${audit.type} Audit`,
        status: audit.status === 'Completed' ? 'completed' as const :
                audit.status === 'InProgress' ? 'in_progress' as const :
                audit.targetDate && audit.targetDate < now ? 'overdue' as const :
                'scheduled' as const,
        date: audit.actualDate || audit.targetDate || now,
        priority: audit.type === 'SOC2' || audit.type === 'PCI' ? 'critical' : 'high',
        assignee: audit.auditor,
        progress: audit.status === 'Completed' ? 100 : audit.status === 'InProgress' ? 50 : 0,
        score: audit.score,
        findings: audit.findings
      })),
      ...checklists.map(checklist => ({
        id: checklist.id,
        type: 'checklist' as const,
        title: checklist.title,
        status: checklist.status === 'Completed' ? 'completed' as const :
                checklist.status === 'InProgress' ? 'in_progress' as const :
                checklist.dueDate < now ? 'overdue' as const : 'scheduled' as const,
        date: checklist.dueDate,
        priority: 'medium' as const,
        progress: checklist.progress
      }))
    ];

    // Generate milestones
    const milestones: Milestone[] = [
      {
        id: 'soc2-annual',
        title: 'SOC 2 Type II Annual Audit',
        date: now + (180 * 24 * 60 * 60 * 1000),
        status: 'upcoming',
        impact: 'high',
        description: 'Annual SOC 2 Type II certification renewal'
      },
      {
        id: 'pci-annual',
        title: 'PCI DSS Annual Assessment',
        date: now + (120 * 24 * 60 * 60 * 1000),
        status: 'upcoming',
        impact: 'critical',
        description: 'Annual PCI DSS compliance assessment'
      }
    ];

    // Generate deadlines
    const deadlines: Deadline[] = [
      {
        id: 'regulatory-filing',
        title: 'Q4 Regulatory Filing',
        date: now + (30 * 24 * 60 * 60 * 1000),
        type: 'regulatory',
        severity: 'critical',
        daysRemaining: 30
      },
      ...checklists
        .filter(c => c.status !== 'Completed')
        .map(checklist => ({
          id: checklist.id,
          title: checklist.title,
          date: checklist.dueDate,
          type: 'internal' as const,
          severity: checklist.dueDate < now + (7 * 24 * 60 * 60 * 1000) ? 'critical' :
                   checklist.dueDate < now + (30 * 24 * 60 * 60 * 1000) ? 'high' : 'medium',
          daysRemaining: Math.ceil((checklist.dueDate - now) / (24 * 60 * 60 * 1000))
        }))
    ];

    return {
      pastEvents: events.filter(e => e.date < now).sort((a, b) => b.date - a.date),
      currentEvents: events.filter(e => e.status === 'in_progress'),
      upcomingEvents: events.filter(e => e.date >= now && e.date <= ninetyDaysFromNow)
                          .sort((a, b) => a.date - b.date),
      milestones,
      deadlines
    };
  }, [audits, checklists]);

  // Sample findings data
  const findings: Finding[] = useMemo(() => {
    return [
      {
        id: 'f1',
        auditId: 'audit1',
        category: 'Access Control',
        severity: 'high',
        description: 'Multi-factor authentication not enforced for privileged accounts',
        recommendation: 'Implement MFA for all administrative and privileged access',
        status: 'open',
        assignee: 'Security Team',
        dueDate: Date.now() + (30 * 24 * 60 * 60 * 1000),
        created: Date.now() - (7 * 24 * 60 * 60 * 1000),
        riskImpact: 85
      },
      {
        id: 'f2',
        auditId: 'audit2',
        category: 'Encryption',
        severity: 'medium',
        description: 'Some data transmissions lack proper encryption',
        recommendation: 'Implement TLS 1.3 for all external communications',
        status: 'in_progress',
        assignee: 'Infrastructure Team',
        dueDate: Date.now() + (45 * 24 * 60 * 60 * 1000),
        created: Date.now() - (14 * 24 * 60 * 60 * 1000),
        riskImpact: 60
      }
    ];
  }, []);

  const getStatusColor = (status: string): string => {
    const colors = {
      'completed': 'text-green-400 bg-green-900/20 border-green-800/30',
      'in_progress': 'text-blue-400 bg-blue-900/20 border-blue-800/30',
      'scheduled': 'text-yellow-400 bg-yellow-900/20 border-yellow-800/30',
      'overdue': 'text-red-400 bg-red-900/20 border-red-800/30',
      'effective': 'text-emerald-400 bg-emerald-900/20 border-emerald-800/30',
      'adequate': 'text-green-400 bg-green-900/20 border-green-800/30',
      'developing': 'text-yellow-400 bg-yellow-900/20 border-yellow-800/30',
      'needs_improvement': 'text-red-400 bg-red-900/20 border-red-800/30'
    };
    return colors[status as keyof typeof colors] || 'text-slate-400 bg-slate-900/20 border-slate-800/30';
  };

  const getSeverityColor = (severity: string): string => {
    const colors = {
      'critical': 'text-red-400 bg-red-900/20 border-red-800/30',
      'high': 'text-orange-400 bg-orange-900/20 border-orange-800/30',
      'medium': 'text-yellow-400 bg-yellow-900/20 border-yellow-800/30',
      'low': 'text-green-400 bg-green-900/20 border-green-800/30'
    };
    return colors[severity as keyof typeof colors] || 'text-slate-400 bg-slate-900/20 border-slate-800/30';
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Executive Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">Compliance Score</div>
            <div className="text-xs px-2 py-1 bg-emerald-900/30 text-emerald-300 rounded border border-emerald-800/30">
              {complianceMetrics.readinessLevel.replace('_', ' ').toUpperCase()}
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {complianceMetrics.overallScore}%
          </div>
          <div className="flex items-center space-x-2">
            <div className={`text-xs px-2 py-1 rounded border ${getStatusColor(complianceMetrics.trendDirection)}`}>
              {complianceMetrics.trendDirection === 'improving' ? '↗️' :
               complianceMetrics.trendDirection === 'declining' ? '↘️' : '→'} {complianceMetrics.trendDirection}
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">Active Audits</div>
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {auditTimeline.currentEvents.length}
          </div>
          <div className="text-sm text-slate-400">
            {auditTimeline.upcomingEvents.length} scheduled
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">Critical Findings</div>
            <span className="text-xs text-red-400">
              {findings.filter(f => f.severity === 'critical').length} critical
            </span>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {complianceMetrics.criticalGaps}
          </div>
          <div className="text-sm text-slate-400">
            {complianceMetrics.remediationBacklog} in backlog
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-400">Compliance Budget</div>
            <span className="text-xs text-green-400">
              Within budget
            </span>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {formatCurrency(budget * 0.15)}
          </div>
          <div className="text-sm text-slate-400">
            15% of total budget allocated
          </div>
        </div>
      </div>

      {/* Compliance Heat Map */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Compliance Framework Status</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'SOC 2 Type II', score: 92, status: 'effective', lastAudit: '2024-09-15' },
              { name: 'PCI DSS', score: 88, status: 'adequate', lastAudit: '2024-08-20' },
              { name: 'GLBA', score: 95, status: 'effective', lastAudit: '2024-10-01' },
              { name: 'BSA/AML', score: 87, status: 'adequate', lastAudit: '2024-09-01' },
              { name: 'State Regulations', score: 83, status: 'developing', lastAudit: '2024-07-15' },
              { name: 'Internal Controls', score: 90, status: 'effective', lastAudit: '2024-10-10' }
            ].map(framework => (
              <div key={framework.name} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-white font-medium">{framework.name}</div>
                    <div className="text-xs text-slate-400">Last audit: {framework.lastAudit}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(framework.status)}`}>
                    {framework.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Compliance Score</span>
                    <span className={`font-medium ${
                      framework.score >= 90 ? 'text-green-400' :
                      framework.score >= 75 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {framework.score}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        framework.score >= 90 ? 'bg-green-500' :
                        framework.score >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${framework.score}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Critical Findings & Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Critical Findings</h3>
            <span className="text-xs text-red-400 bg-red-900/20 px-2 py-1 rounded border border-red-800/30">
              {findings.filter(f => f.severity === 'critical' || f.severity === 'high').length} items
            </span>
          </div>
          <div className="p-4 space-y-3">
            {findings
              .filter(f => f.severity === 'critical' || f.severity === 'high')
              .slice(0, 3)
              .map(finding => (
              <div key={finding.id} className={`p-3 rounded-lg border ${getSeverityColor(finding.severity)}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-white text-sm">{finding.category}</div>
                    <div className="text-xs text-slate-300 mt-1">{finding.description}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-black/20 border border-current/30">
                    {finding.severity.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-xs text-slate-400">
                    Assignee: {finding.assignee}
                  </div>
                  <div className="text-xs text-slate-400">
                    Due: {formatDate(finding.dueDate)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Upcoming Deadlines</h3>
            <span className="text-xs text-yellow-400 bg-yellow-900/20 px-2 py-1 rounded border border-yellow-800/30">
              {auditTimeline.deadlines.length} deadlines
            </span>
          </div>
          <div className="p-4 space-y-3">
            {auditTimeline.deadlines
              .sort((a, b) => a.daysRemaining - b.daysRemaining)
              .slice(0, 4)
              .map(deadline => (
              <div key={deadline.id} className={`p-3 rounded-lg border ${
                deadline.daysRemaining < 7 ? 'bg-red-900/20 border-red-800/30' :
                deadline.daysRemaining < 30 ? 'bg-orange-900/20 border-orange-800/30' :
                'bg-slate-700/50 border-slate-600'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-white text-sm">{deadline.title}</div>
                    <div className="text-xs text-slate-300 mt-1 capitalize">{deadline.type}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-medium ${
                      deadline.daysRemaining < 7 ? 'text-red-400' :
                      deadline.daysRemaining < 30 ? 'text-orange-400' : 'text-yellow-400'
                    }`}>
                      {deadline.daysRemaining} days
                    </div>
                    <div className="text-xs text-slate-400">
                      {formatDate(deadline.date)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAudits = () => (
    <div className="space-y-6">
      {/* Audit Management Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Audit Management</h3>
        <div className="flex space-x-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-700 text-white px-3 py-2 rounded border border-slate-600 text-sm"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="scheduled">Scheduled</option>
            <option value="overdue">Overdue</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">
            Schedule New Audit
          </button>
        </div>
      </div>

      {/* Audit Pipeline */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h4 className="text-md font-medium text-white">Audit Pipeline</h4>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { status: 'Scheduled', count: audits.filter(a => a.status === 'NotStarted').length, color: 'text-yellow-400' },
              { status: 'Preparation', count: audits.filter(a => a.status === 'InProgress').length, color: 'text-blue-400' },
              { status: 'Execution', count: 0, color: 'text-orange-400' },
              { status: 'Reporting', count: audits.filter(a => a.status === 'Completed').length, color: 'text-green-400' }
            ].map(stage => (
              <div key={stage.status} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 text-center">
                <div className={`text-2xl font-bold ${stage.color}`}>{stage.count}</div>
                <div className="text-sm text-slate-400">{stage.status}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Audit Table */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Audit</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Type</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Status</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Auditor</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Schedule</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Score</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Findings</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Cost</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {audits
                .filter(audit => filterStatus === 'all' ||
                  (filterStatus === audit.status.toLowerCase().replace('_', '')) ||
                  (filterStatus === 'in_progress' && audit.status === 'InProgress') ||
                  (filterStatus === 'scheduled' && audit.status === 'NotStarted'))
                .map(audit => (
                <tr key={audit.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-3 px-4">
                    <div>
                      <div className="text-white font-medium">{audit.type} Audit</div>
                      <div className="text-xs text-slate-400">ID: {audit.id}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded border border-slate-600">
                      {audit.type}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(audit.status.toLowerCase())}`}>
                      {audit.status.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-300">{audit.auditor}</td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-white">
                      {audit.targetDate ? formatDate(audit.targetDate) : 'Not scheduled'}
                    </div>
                    {audit.actualDate && (
                      <div className="text-xs text-green-400">Completed: {formatDate(audit.actualDate)}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {audit.status === 'Completed' ? (
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${
                          audit.score >= 90 ? 'text-green-400' :
                          audit.score >= 75 ? 'text-blue-400' :
                          audit.score >= 60 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {audit.score}%
                        </span>
                        <div className="w-8 h-2 bg-slate-700 rounded-full">
                          <div
                            className={`h-2 rounded-full ${
                              audit.score >= 90 ? 'bg-green-500' :
                              audit.score >= 75 ? 'bg-blue-500' :
                              audit.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${audit.score}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="text-xs text-red-400">{audit.findings.critical} critical</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        <span className="text-xs text-orange-400">{audit.findings.high} high</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-xs text-yellow-400">{audit.findings.medium} medium</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-white">{formatCurrency(audit.cost)}</td>
                  <td className="py-3 px-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedAudit(audit)}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        View
                      </button>
                      {audit.remediationRequired && (
                        <button className="text-orange-400 hover:text-orange-300 text-sm">
                          Remediate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Compliance Management</h3>

      {/* Framework Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="p-4 border-b border-slate-700">
            <h4 className="text-md font-medium text-white">Compliance Frameworks</h4>
          </div>
          <div className="p-4 space-y-3">
            {[
              { name: 'SOC 2 Type II', scope: 'Security, Availability, Confidentiality', frequency: 'Annual', next: '2025-06-15' },
              { name: 'PCI DSS v4.0', scope: 'Payment Card Data Security', frequency: 'Annual', next: '2025-03-20' },
              { name: 'GLBA', scope: 'Financial Privacy', frequency: 'Continuous', next: 'Ongoing' },
              { name: 'BSA/AML', scope: 'Anti-Money Laundering', frequency: 'Continuous', next: 'Ongoing' }
            ].map(framework => (
              <div key={framework.name} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-white font-medium">{framework.name}</div>
                    <div className="text-xs text-slate-400 mt-1">{framework.scope}</div>
                    <div className="text-xs text-slate-500 mt-1">Frequency: {framework.frequency}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-400">Next Assessment</div>
                    <div className="text-sm text-white">{framework.next}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg border border-slate-700">
          <div className="p-4 border-b border-slate-700">
            <h4 className="text-md font-medium text-white">Compliance Maturity</h4>
          </div>
          <div className="p-4 space-y-4">
            {[
              { domain: 'Policy & Procedures', maturity: 85, trend: 'improving' },
              { domain: 'Risk Assessment', maturity: 78, trend: 'stable' },
              { domain: 'Control Implementation', maturity: 92, trend: 'improving' },
              { domain: 'Monitoring & Testing', maturity: 70, trend: 'developing' },
              { domain: 'Incident Response', maturity: 88, trend: 'improving' }
            ].map(domain => (
              <div key={domain.domain} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-300">{domain.domain}</span>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${
                      domain.maturity >= 85 ? 'text-green-400' :
                      domain.maturity >= 70 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {domain.maturity}%
                    </span>
                    <span className={`text-xs ${
                      domain.trend === 'improving' ? 'text-green-400' :
                      domain.trend === 'declining' ? 'text-red-400' : 'text-slate-400'
                    }`}>
                      {domain.trend === 'improving' ? '↗️' : domain.trend === 'declining' ? '↘️' : '→'}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      domain.maturity >= 85 ? 'bg-green-500' :
                      domain.maturity >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${domain.maturity}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Checklists */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700 flex justify-between items-center">
          <h4 className="text-md font-medium text-white">Active Compliance Checklists</h4>
          <button className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-medium">
            Create Checklist
          </button>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {checklists.map(checklist => (
              <div key={checklist.id} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="text-white font-medium">{checklist.title}</div>
                    <div className="text-xs text-slate-400">{checklist.category}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(checklist.status.toLowerCase())}`}>
                    {checklist.status.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span>Progress</span>
                      <span>{checklist.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-600 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${checklist.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">
                      {checklist.items.filter(item => item.completed).length} / {checklist.items.length} items
                    </span>
                    <span className="text-slate-300">
                      Due: {formatDate(checklist.dueDate)}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-600 flex space-x-2">
                  <button
                    onClick={() => setSelectedChecklist(checklist)}
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    View Details
                  </button>
                  <button className="text-slate-400 hover:text-slate-300 text-sm">
                    Export
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimeline = () => (
    <div className="space-y-6">
      {/* Timeline Controls */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-white">Audit Timeline</h3>
        <div className="flex space-x-3">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-slate-300">View:</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as any)}
              className="bg-slate-700 text-white px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="calendar">Calendar</option>
              <option value="list">List</option>
              <option value="gantt">Gantt</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-slate-300">Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="bg-slate-700 text-white px-3 py-2 rounded border border-slate-600 text-sm"
            >
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
              <option value="6m">6 Months</option>
              <option value="1y">1 Year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Timeline View */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center space-x-4">
            <h4 className="text-md font-medium text-white">Audit Events</h4>
            <div className="flex space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-slate-400">Completed</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-slate-400">In Progress</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-slate-400">Scheduled</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <span className="text-slate-400">Overdue</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Past Events */}
          <div>
            <h5 className="text-sm font-medium text-slate-400 mb-3">Recently Completed</h5>
            <div className="space-y-2">
              {auditTimeline.pastEvents.slice(0, 3).map(event => (
                <div key={event.id} className="flex items-center space-x-3 p-3 bg-slate-700/30 rounded-lg border-l-4 border-green-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{event.title}</div>
                    <div className="text-xs text-slate-400">
                      {event.assignee && `Auditor: ${event.assignee} • `}
                      Completed: {formatDate(event.date)}
                      {event.score && ` • Score: ${event.score}%`}
                    </div>
                  </div>
                  <div className="text-sm text-green-400">Completed</div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Events */}
          <div>
            <h5 className="text-sm font-medium text-slate-400 mb-3">Current Activities</h5>
            <div className="space-y-2">
              {auditTimeline.currentEvents.map(event => (
                <div key={event.id} className="flex items-center space-x-3 p-3 bg-blue-900/20 rounded-lg border-l-4 border-blue-400">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{event.title}</div>
                    <div className="text-xs text-slate-400">
                      {event.assignee && `Assigned to: ${event.assignee} • `}
                      {event.progress && `Progress: ${event.progress}%`}
                    </div>
                    {event.progress && (
                      <div className="mt-2 w-full bg-slate-700 rounded-full h-1">
                        <div
                          className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                          style={{ width: `${event.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-blue-400">In Progress</div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div>
            <h5 className="text-sm font-medium text-slate-400 mb-3">Upcoming Schedule</h5>
            <div className="space-y-2">
              {auditTimeline.upcomingEvents.slice(0, 4).map(event => (
                <div key={event.id} className="flex items-center space-x-3 p-3 bg-slate-700/30 rounded-lg border-l-4 border-yellow-400">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{event.title}</div>
                    <div className="text-xs text-slate-400">
                      {event.assignee && `Auditor: ${event.assignee} • `}
                      Scheduled: {formatDate(event.date)}
                    </div>
                  </div>
                  <div className="text-sm text-yellow-400">Scheduled</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Milestones */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h4 className="text-md font-medium text-white">Key Milestones</h4>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {auditTimeline.milestones.map(milestone => (
              <div key={milestone.id} className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-lg">
                <div className={`w-3 h-3 rounded-full ${
                  milestone.impact === 'high' ? 'bg-red-400' :
                  milestone.impact === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                }`}></div>
                <div className="flex-1">
                  <div className="text-white font-medium">{milestone.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{milestone.description}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-white">{formatDate(milestone.date)}</div>
                  <div className="text-xs text-slate-400 capitalize">{milestone.impact} impact</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Compliance Reports</h3>

      {/* Report Generation */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h4 className="text-md font-medium text-white">Generate Reports</h4>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { type: 'Executive Summary', description: 'High-level compliance overview for leadership', icon: '📊' },
              { type: 'Detailed Audit Report', description: 'Comprehensive audit findings and recommendations', icon: '📋' },
              { type: 'Compliance Dashboard', description: 'Real-time compliance metrics and KPIs', icon: '📈' },
              { type: 'Risk Assessment', description: 'Current risk landscape and mitigation strategies', icon: '⚠️' },
              { type: 'Remediation Status', description: 'Status of audit findings and corrective actions', icon: '🔧' },
              { type: 'Regulatory Filing', description: 'Reports required for regulatory submissions', icon: '📄' }
            ].map(report => (
              <div key={report.type} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-blue-600/50 transition-colors cursor-pointer">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-2xl">{report.icon}</span>
                  <div>
                    <div className="text-white font-medium">{report.type}</div>
                    <div className="text-xs text-slate-400 mt-1">{report.description}</div>
                  </div>
                </div>
                <button className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm">
                  Generate
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-4 border-b border-slate-700">
          <h4 className="text-md font-medium text-white">Recent Reports</h4>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {[
              { title: 'Q3 2024 Compliance Summary', type: 'Executive Summary', date: '2024-10-15', status: 'final', score: 87 },
              { title: 'SOC 2 Type II Audit Report', type: 'Audit Report', date: '2024-09-20', status: 'submitted', score: 92 },
              { title: 'PCI DSS Assessment Results', type: 'Risk Assessment', date: '2024-08-10', status: 'final', score: 88 },
              { title: 'Remediation Progress Report', type: 'Remediation Status', date: '2024-07-25', status: 'draft', score: 75 }
            ].map((report, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg border border-slate-600">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="text-white font-medium">{report.title}</div>
                    <div className="text-xs text-slate-400">{report.type} • Generated: {report.date}</div>
                  </div>
                  {report.score && (
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${
                        report.score >= 90 ? 'text-green-400' :
                        report.score >= 75 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {report.score}%
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`text-xs px-2 py-1 rounded ${
                    report.status === 'final' ? 'bg-green-900/20 text-green-300' :
                    report.status === 'submitted' ? 'bg-blue-900/20 text-blue-300' :
                    'bg-yellow-900/20 text-yellow-300'
                  }`}>
                    {report.status}
                  </span>
                  <button className="text-blue-400 hover:text-blue-300 text-sm">
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSOC2Tab = () => (
    <div className="space-y-6">
      {/* SOC 2 Overview */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <span className="text-2xl">🔒</span>
              SOC 2 Compliance Center
            </h3>
            <p className="text-slate-400 text-sm mt-1">Interactive controls mapping and security-compliance balancing</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowSOC2Dashboard(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors text-sm"
            >
              Open SOC 2 Dashboard
            </button>
            <button
              onClick={() => setShowSOC2Scenario(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded transition-colors text-sm"
            >
              Start Scenario
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <div className="text-2xl font-bold text-blue-400">92%</div>
            <div className="text-sm text-slate-400">SOC 2 Readiness</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <div className="text-2xl font-bold text-green-400">6</div>
            <div className="text-sm text-slate-400">Controls Implemented</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <div className="text-2xl font-bold text-orange-400">3</div>
            <div className="text-sm text-slate-400">Critical Gaps</div>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600">
            <div className="text-2xl font-bold text-purple-400">85%</div>
            <div className="text-sm text-Slate-400">Balance Score</div>
          </div>
        </div>

        {/* Categories Overview */}
        <div>
          <h4 className="text-white font-medium mb-4">SOC 2 Trust Services Categories</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { category: 'Security', maturity: 95, implemented: 2, total: 3, color: 'text-red-400' },
              { category: 'Availability', maturity: 88, implemented: 1, total: 2, color: 'text-green-400' },
              { category: 'Confidentiality', maturity: 92, implemented: 2, total: 2, color: 'text-blue-400' },
              { category: 'Processing Integrity', maturity: 85, implemented: 1, total: 2, color: 'text-yellow-400' },
              { category: 'Privacy', maturity: 78, implemented: 0, total: 1, color: 'text-purple-400' }
            ].map(cat => (
              <div key={cat.category} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                <div className="flex justify-between items-center mb-3">
                  <div className={`text-lg font-semibold ${cat.color}`}>{cat.category}</div>
                  <div className="text-xs text-slate-400">{cat.implemented}/{cat.total}</div>
                </div>
                <div className="w-full bg-slate-600 rounded-full h-2 mb-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                    style={{ width: `${cat.maturity}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-400">Maturity: {cat.maturity}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="border-t border-slate-700 pt-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-400">
              <span className="text-yellow-400">⚠️</span> 3 controls require immediate attention
            </div>
            <button
              onClick={() => setShowSOC2Dashboard(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors text-sm"
            >
              Manage Controls
            </button>
          </div>
        </div>
      </div>

      {/* Security vs. Compliance Balance */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Security-Compliance Balance Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-white font-medium mb-3">Current Balance</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-red-400">Security Focus</span>
                  <span className="text-white font-medium">65%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500 h-3 rounded-full"
                    style={{ width: '85%', backgroundPosition: '35% center' }}
                  ></div>
                </div>
              </div>
              <div className="flex justify-between text-sm mt-3">
                <span className="text-slate-400">Balance Score:</span>
                <span className="text-green-400 font-medium">Well Balanced</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-white font-medium mb-3">Recommendations</h4>
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <span className="text-green-400 mt-1">→</span>
                <div>
                  <div className="text-sm text-white font-medium">Maintain current balance</div>
                  <div className="text-xs text-slate-400">Good security-compliance alignment</div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 mt-1">→</span>
                <div>
                  <div className="text-sm text-white font-medium">Focus on privacy controls</div>
                  <div className="text-xs text-slate-400">Improve privacy category maturity</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent SOC 2 Activities</h3>
        <div className="space-y-3">
          {[
            { action: 'Control Tested: CC6.1 - Access Controls', time: '2 hours ago', status: 'success' },
            { action: 'Implemented: CC5.1 - Confidential Data Management', time: '1 day ago', status: 'success' },
            { action: 'Gap Identified: CC7.1 - Privacy Notice', time: '3 days ago', status: 'warning' },
            { action: 'Risk Assessment Completed', time: '5 days ago', status: 'success' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="flex items-center space-x-3">
                <span className={`text-lg ${
                  activity.status === 'success' ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {activity.status === 'success' ? '✓' : '⚠️'}
                </span>
                <div>
                  <div className="text-sm text-white">{activity.action}</div>
                  <div className="text-xs text-slate-400">{activity.time}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <EnterpriseDashboardShell
        title="Audit & Compliance Management"
        subtitle="Advanced compliance monitoring and audit management system"
        onClose={onClose}
        testId="audit-dashboard-enhanced"
        headerExtra={
          <div className="text-right">
            <div className="text-xs text-slate-400">Compliance Budget</div>
            <div className="text-lg font-bold text-green-400">{formatCurrency(budget * 0.15)}</div>
          </div>
        }
        tabs={
          <div className="flex border-b border-slate-700 bg-slate-800/50 shrink-0 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'audits', label: 'Audits', icon: '🔍' },
              { id: 'compliance', label: 'Compliance', icon: '✅' },
              { id: 'timeline', label: 'Timeline', icon: '📅' },
              { id: 'reports', label: 'Reports', icon: '📄' },
              { id: 'soc2', label: 'SOC 2', icon: '🔒' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400 bg-slate-800'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.id === 'compliance' && complianceMetrics.criticalGaps > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {complianceMetrics.criticalGaps}
                  </span>
                )}
              </button>
            ))}
          </div>
        }
      >
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'audits' && renderAudits()}
        {activeTab === 'compliance' && renderCompliance()}
        {activeTab === 'timeline' && renderTimeline()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'soc2' && renderSOC2Tab()}
      </EnterpriseDashboardShell>

      {showSOC2Dashboard && (
        <SOC2DashboardEnhanced
          budget={budget}
          onClose={() => setShowSOC2Dashboard(false)}
          onScenarioComplete={(result) => {
            console.log('SOC2 Scenario completed:', result);
            setShowSOC2Scenario(false);
          }}
        />
      )}

      {/* SOC 2 Scenario Player Modal */}
      {showSOC2Scenario && (
        <SOC2ScenarioPlayer
          scenario={createDefaultScenario()}
          budget={budget}
          onComplete={(result) => {
            console.log('SOC2 Scenario result:', result);
            setShowSOC2Scenario(false);
          }}
          onClose={() => setShowSOC2Scenario(false)}
        />
      )}
    </>
  );
};

const createDefaultScenario = () => ({
  id: 'audit-prep-scenario',
  title: 'SOC 2 Audit Preparation',
  description: 'Prepare your organization for a SOC 2 Type II audit',
  category: 'audit_preparation' as const,
  difficulty: 'intermediate' as const,
  businessContext: {
    companySize: 'medium' as const,
    industry: 'fintech',
    regulatoryEnvironment: ['SOX', 'GLBA'],
    customerProfile: {
      sensitive: true,
      regulated: true,
      international: false,
      enterpriseLevel: true,
      dataVolume: 'medium' as const
    },
    riskProfile: { riskTolerance: 50, riskCapacity: 500000 },
    businessPriorities: ['growth', 'compliance', 'security']
  },
  threatLandscape: [],
  availableControls: ['cc1.1', 'cc6.1', 'cc4.1', 'cc5.1'],
  targetControls: [],
  constraints: {
    budget: 250000,
    timeLimit: 180,
    availableResources: [],
    regulatoryRequirements: [],
    businessDisruption: 20,
    stakeholderExpectations: []
  },
  objectives: [
    { id: 'obj1', description: 'Implement required SOC 2 controls', targetValue: 4, currentValue: 0 },
    { id: 'obj2', description: 'Achieve 90% audit readiness', targetValue: 90, currentValue: 0 }
  ],
  successMetrics: [
    { id: 'metric1', name: 'Control Implementation', weight: 0.4 },
    { id: 'metric2', name: 'Compliance Score', weight: 0.3 },
    { id: 'metric3', name: 'Time Management', weight: 0.3 }
  ],
  timeLimit: 180,
  costBudget: 250000,
  riskTolerance: 40
});

export default AuditDashboardEnhanced;